import { useState, useEffect } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { CreditCard, CheckCircle, AlertCircle } from 'lucide-react';
import FormPageLayout from '../components/common/FormPageLayout';
import Alert from '../components/common/Alert';
import DateTimeInput from '../components/common/DateTimeInput';
import CalculatorInput from '../components/common/CalculatorInput';
import MobileSelect from '../components/common/MobileSelect';
import { getIconComponent } from '../components/common/IconPicker';
import { useRecordReceivablePayment, useRecordDebtPayment, useAccountsQuery, useReceivableQuery, useDebtQuery } from '../hooks/useQueries';
import { useConfig } from '../context/ConfigContext';
import { useToast } from '../context/ToastContext';
import { formatDate } from '../utils/formatters';

const PAYMENT_METHOD_OPTIONS = [
  { value: 'cash', label: 'Cash' },
  { value: 'bank_transfer', label: 'Bank Transfer' },
  { value: 'upi', label: 'UPI' },
  { value: 'other', label: 'Other' }
];

const initialFormData = {
  amount: '',
  paymentDate: formatDate(new Date(), 'input'),
  paymentMethod: 'cash',
  notes: '',
  createTransaction: false,
  accountId: ''
};

export default function RecordPaymentPage() {
  const navigate = useNavigate();
  const { id } = useParams();
  const location = useLocation();

  // Determine type from URL path
  const type = location.pathname.includes('/debts/') ? 'debt' : 'receivable';

  const { formatAmount, config } = useConfig();
  const { toast } = useToast();
  const recordReceivablePayment = useRecordReceivablePayment();
  const recordDebtPayment = useRecordDebtPayment();
  const { data: accounts = [] } = useAccountsQuery();

  // Fetch fresh data
  const { data: receivableData, isLoading: receivableLoading } = useReceivableQuery(
    id,
    { enabled: type === 'receivable' && !!id }
  );
  const { data: debtData, isLoading: debtLoading } = useDebtQuery(
    id,
    { enabled: type === 'debt' && !!id }
  );

  const entity = type === 'receivable'
    ? (receivableData?.receivable || receivableData)
    : (debtData?.debt || debtData);

  const [formData, setFormData] = useState(initialFormData);
  const [loading, setLoading] = useState(false);
  const [validationErrors, setValidationErrors] = useState({});
  const [error, setError] = useState(null);

  const recordMutation = type === 'receivable' ? recordReceivablePayment : recordDebtPayment;
  const remaining = entity ? (entity.amount || 0) - (entity.paidAmount || 0) : 0;
  const activeAccounts = accounts.filter(a => !a.isDeleted && a.isActive !== false);

  // Build account options with icons
  const accountOptions = activeAccounts.map(acc => ({
    value: acc._id,
    label: acc.name,
    icon: getIconComponent(acc.icon),
    iconColor: acc.color
  }));

  // Set default amount to remaining balance when entity loads
  useEffect(() => {
    if (entity && remaining > 0) {
      setFormData(prev => ({
        ...prev,
        amount: String(parseFloat(remaining.toFixed(2)))
      }));
    }
  }, [entity, remaining]);

  const handleChange = (e) => {
    const { name, value, type: inputType, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: inputType === 'checkbox' ? checked : value
    }));
    if (validationErrors[name]) {
      setValidationErrors(prev => ({ ...prev, [name]: null }));
    }
  };

  const validateForm = () => {
    const errors = {};
    const paymentAmount = parseFloat(formData.amount);

    if (!formData.amount || paymentAmount <= 0) {
      errors.amount = 'Please enter a valid amount';
    } else if (paymentAmount > remaining) {
      errors.amount = `Amount cannot exceed remaining balance (${formatAmount(remaining)})`;
    }
    if (!formData.paymentDate) {
      errors.paymentDate = 'Payment date is required';
    }
    if (formData.createTransaction && !formData.accountId) {
      errors.accountId = 'Please select an account';
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) return;

    setLoading(true);
    setError(null);

    try {
      const payload = {
        amount: parseFloat(formData.amount),
        paymentDate: formData.paymentDate,
        paymentMethod: formData.paymentMethod || undefined,
        notes: formData.notes?.trim() || '',
        createTransaction: formData.createTransaction
      };

      if (formData.createTransaction && formData.accountId) {
        payload.accountId = formData.accountId;
      }

      await recordMutation.mutateAsync({ id, data: payload });
      toast.success('Payment recorded successfully');
      navigate(type === 'receivable' ? `/receivables/${id}` : `/debts/${id}`);
    } catch (err) {
      let errorMessage = 'Failed to record payment';
      if (err.error) {
        errorMessage = err.error;
      } else if (err.errors && Array.isArray(err.errors)) {
        const fieldErrors = err.errors.map(e => {
          const fieldLabels = {
            amount: 'Amount',
            paymentDate: 'Payment date',
            paymentMethod: 'Payment method',
            accountId: 'Account',
            notes: 'Notes'
          };
          const label = fieldLabels[e.field] || e.field;
          return `${label}: ${e.message.replace(/Expected.*received.*/, 'Invalid value')}`;
        });
        errorMessage = fieldErrors.join('. ');
      }
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  if (receivableLoading || debtLoading) {
    return (
      <div className="flex items-center justify-center min-h-[50vh]">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  if (!entity) {
    return (
      <div className="text-center py-12">
        <p className="text-surface-600">{type === 'receivable' ? 'Receivable' : 'Debt'} not found</p>
        <button
          onClick={() => navigate(type === 'receivable' ? '/receivables' : '/debts')}
          className="mt-4 btn-primary"
        >
          Go Back
        </button>
      </div>
    );
  }

  const isReceivable = type === 'receivable';
  const accentColor = isReceivable ? config.incomeColor : config.expenseColor;

  const paymentAmount = parseFloat(formData.amount) || 0;
  const exceedsRemaining = paymentAmount > remaining;
  const willSettleFully = paymentAmount > 0 && paymentAmount === remaining;

  const backPath = type === 'receivable' ? `/receivables/${id}` : `/debts/${id}`;

  return (
    <FormPageLayout title="Record Payment" backPath={backPath}>
      <form onSubmit={handleSubmit} className="space-y-4">
        {error && (
          <Alert type="error" message={error} onClose={() => setError(null)} />
        )}

        {/* Summary */}
        <div className="bg-surface-50 rounded-xl p-3">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm text-surface-600">
              {isReceivable ? 'From' : 'To'}: {entity.contact?.name}
            </span>
          </div>
          <div className="flex items-center justify-between">
            <span className="text-sm text-surface-500">Remaining Balance</span>
            <span className="font-bold" style={{ color: accentColor }}>
              {formatAmount(remaining)}
            </span>
          </div>
        </div>

        {/* Amount */}
        <div>
          <label className="label">Payment Amount *</label>
          <CalculatorInput
            name="amount"
            value={formData.amount}
            onChange={(e) => {
              const val = e?.target?.value ?? e;
              setFormData(prev => ({ ...prev, amount: val }));
              if (validationErrors.amount) {
                setValidationErrors(prev => ({ ...prev, amount: null }));
              }
            }}
            placeholder="Enter amount"
            className="input"
            error={validationErrors.amount}
          />
          {remaining > 0 && !willSettleFully && (
            <button
              type="button"
              onClick={() => setFormData(prev => ({ ...prev, amount: String(parseFloat(remaining.toFixed(2))) }))}
              className="text-xs mt-1 text-primary-600 hover:text-primary-700"
            >
              Use full amount ({formatAmount(remaining)})
            </button>
          )}
        </div>

        {/* Amount exceeds error */}
        {exceedsRemaining && (
          <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-100 rounded-xl">
            <AlertCircle className="w-4 h-4 text-red-600 flex-shrink-0" />
            <p className="text-sm text-red-700">
              Amount cannot exceed remaining balance ({formatAmount(remaining)})
            </p>
          </div>
        )}

        {/* Auto-settle notice */}
        {willSettleFully && !exceedsRemaining && (
          <div className="flex items-center gap-2 p-3 bg-green-50 border border-green-100 rounded-xl">
            <CheckCircle className="w-4 h-4 text-green-600 flex-shrink-0" />
            <p className="text-sm text-green-700">
              This payment will mark the {isReceivable ? 'receivable' : 'debt'} as fully settled.
            </p>
          </div>
        )}

        {/* Payment Date */}
        <div>
          <label className="label">Payment Date *</label>
          <DateTimeInput
            dateOnly
            value={formData.paymentDate}
            onChange={(date) => {
              setFormData(prev => ({ ...prev, paymentDate: date }));
              if (validationErrors.paymentDate) {
                setValidationErrors(prev => ({ ...prev, paymentDate: null }));
              }
            }}
            placeholder="Payment Date"
            error={validationErrors.paymentDate}
          />
        </div>

        {/* Payment Method — inline pill chips */}
        <div>
          <label className="label">Payment Method</label>
          <div className="flex flex-wrap gap-1.5">
            {PAYMENT_METHOD_OPTIONS.map(method => (
              <button
                key={method.value}
                type="button"
                onClick={() => setFormData(prev => ({
                  ...prev,
                  paymentMethod: prev.paymentMethod === method.value ? '' : method.value
                }))}
                className={`px-3 py-1.5 rounded-full text-xs font-semibold transition-colors ${
                  formData.paymentMethod === method.value
                    ? 'text-white'
                    : 'bg-surface-100 text-surface-600'
                }`}
                style={formData.paymentMethod === method.value ? { backgroundColor: accentColor } : {}}
              >
                {method.label}
              </button>
            ))}
          </div>
        </div>

        {/* Create Transaction Toggle */}
        <div className="bg-surface-50 rounded-xl p-3">
          <label className="flex items-center gap-3 cursor-pointer">
            <input
              type="checkbox"
              name="createTransaction"
              checked={formData.createTransaction}
              onChange={handleChange}
              className="w-4 h-4 rounded border-surface-300 text-primary-600 focus:ring-primary-500"
            />
            <div>
              <span className="text-sm font-medium text-surface-700">
                Create {isReceivable ? 'income' : 'expense'} transaction
              </span>
              <p className="text-xs text-surface-500">
                Record this in your {isReceivable ? 'income' : 'expenses'}
              </p>
            </div>
          </label>

          {formData.createTransaction && (
            <div className="mt-3 pl-7">
              <label className="label">Account *</label>
              <MobileSelect
                value={formData.accountId}
                onChange={(value) => {
                  setFormData(prev => ({ ...prev, accountId: value }));
                  if (validationErrors.accountId) {
                    setValidationErrors(prev => ({ ...prev, accountId: null }));
                  }
                }}
                options={accountOptions}
                placeholder="Select account"
                label="Select Account"
                columns={2}
                size="sm"
                error={!!validationErrors.accountId}
                showIconInTrigger
              />
            </div>
          )}
        </div>

        {/* Notes */}
        <div>
          <label className="label">Notes</label>
          <textarea
            name="notes"
            value={formData.notes}
            onChange={handleChange}
            rows={2}
            placeholder="Any additional notes..."
            className="input resize-none"
          />
        </div>

        {/* Actions */}
        <div className="flex flex-col-reverse md:flex-row gap-2 md:gap-3 pt-4 border-t border-surface-200">
          <button
            type="button"
            onClick={() => navigate(backPath)}
            className="w-full md:flex-1 px-4 py-2.5 border border-surface-200 rounded-xl text-surface-700 hover:bg-surface-50 transition-colors font-medium"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={loading || exceedsRemaining}
            className="w-full md:flex-1 px-4 py-2.5 rounded-xl text-white font-medium transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
            style={{ backgroundColor: accentColor }}
          >
            <CreditCard className="w-4 h-4" />
            {loading ? 'Recording...' : 'Record Payment'}
          </button>
        </div>
      </form>
    </FormPageLayout>
  );
}
